import { forwardRef, useState } from 'react';
import Avatar from './Avatar';
import classes from './ConversationBubble.module.css';
import Alert from './assets/alert.svg';
import { ReactComponent as Like } from './assets/like.svg';
import { ReactComponent as Dislike } from './assets/dislike.svg';
import { ReactComponent as Copy } from './assets/copy.svg';
import { ReactComponent as CheckMark } from './assets/checkmark.svg';
import { ReactComponent as ReadAloud } from './assets/read_aloud.svg';
import ReactMarkdown from 'react-markdown';
import copy from 'copy-to-clipboard';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/cjs/styles/prism';
import DocsGPT3 from './assets/cute_docsgpt3.svg';
import { Tooltip } from '@mui/material';


// const DisableSourceFE = import.meta.env.VITE_DISABLE_SOURCE_FE || false;
const DisableSourceFE = false;
const apiHost = process.env.REACT_APP_API_URL;

const ConversationBubble = forwardRef(function ConversationBubble(
    { message, type, className, feedback, handleFeedback, sources, isTheLast, avatarPath },
    ref
) {
    const [openSource, setOpenSource] = useState(null);
    const [copied, setCopied] = useState(false);

    const handleCopyClick = (text) => {
        copy(text);
        setCopied(true);
        // Reset copied to false after a few seconds
        setTimeout(() => {
            setCopied(false);
        }, 2000);
    };
    const [isCopyHovered, setIsCopyHovered] = useState(false);
    const [isLikeHovered, setIsLikeHovered] = useState(false);
    const [isDislikeHovered, setIsDislikeHovered] = useState(false);
    const [isReadHovered, setIsReadHovered] = useState(false);
    const [isLikeClicked, setIsLikeClicked] = useState(false);
    const [isDislikeClicked, setIsDislikeClicked] = useState(false);
    const [isReadClicked, setIsReadClicked] = useState(false);
    const [audio, setAudio] = useState(new Audio());

    let bubble;

    if (type === 'QUESTION') {
        bubble = (
            <div ref={ref} className={`flex flex-col items-center self-end md:flex-row-reverse ${className}`}>
                {avatarPath === undefined && <Avatar className="md:self-unset self-end text-2xl md:mt-2" avatar="🧑‍💻" />}
                {avatarPath !== undefined && (
                    <Avatar
                        className="md:self-unset self-end text-2xl md:mt-2"
                        avatar={<img width="100px" className="mb-2" src={`${apiHost + '/image/load?avatar_path=' + avatarPath}`} />}
                    />
                )}
                <div className="ml-10 flex items-center rounded-3xl bg-purple-30 p-3.5 text-white md:mr-2">
                    <div className="whitespace-pre-wrap break-all">
                        <ReactMarkdown>{message}</ReactMarkdown>
                    </div>
                </div>
            </div>
        );
    } else {
        bubble = (
            <>
                <div ref={ref} className={`flex self-start ${className} group flex-col`}>
                    <div className="flex flex-col self-start md:flex-row">
                        <Avatar
                            className="mt-2 h-12 w-12 text-2xl"
                            avatar={<img src={DocsGPT3} alt="DocsGPT" className="h-full w-full object-cover" />}
                        />
                        <div className="flex flex-col md:flex-row">
                            <div
                                className={`flex max-w-[92vw] rounded-3xl bg-gray-1000 p-3.5 md:ml-2 md:mr-4 ${
                                    type === 'ERROR'
                                        ? 'flex-row items-center rounded-full border border-transparent bg-[#FFE7E7] p-2 py-5 text-sm font-normal text-red-3000  dark:border-red-2000 dark:text-white'
                                        : 'flex-col rounded-3xl'
                                }`}
                            >
                                {type === 'ERROR' && <img src={Alert} alt="alert" className="mr-2 inline" />}
                                <div className="max-w-screen-md whitespace-pre-wrap break-words">
                                    <ReactMarkdown
                                        components={{
                                        code({ node, inline, className, children, ...props }) {
                                            const match = /language-(\w+)/.exec(className || '');

                                            return !inline && match ? (
                                                <SyntaxHighlighter PreTag="div" language={match[1]} {...props} style={vscDarkPlus}>
                                                    {String(children).replace(/\n$/, '')}
                                                </SyntaxHighlighter>
                                            ) : (
                                                <code className={className ? className : ''} {...props}>
                                                    {children}
                                                </code>
                                            );
                                        },
                                        ul({ children }) {
                                            return (
                                                <ul className={`list-inside list-disc whitespace-normal pl-4 ${classes.list}`}>
                                                    {children}
                                                </ul>
                                            );
                                        },
                                        ol({ children }) {
                                            return (
                                                <ol className={`list-inside list-decimal whitespace-normal pl-4 ${classes.list}`}>
                                                    {children}
                                                </ol>
                                            );
                                        }
                                    }}
                                    >
                                        {message}
                                    </ReactMarkdown>
                                </div>
                                {DisableSourceFE || type === 'ERROR' ? null : (
                                    <>
                                        <span className="mt-3 h-px w-full bg-[#DEDEDE]"></span>
                                        <div className="mt-3 flex w-full flex-row flex-wrap items-center justify-start gap-2 overflow-x-auto">
                                            <div className="py-1 text-base font-semibold">Sources:</div>
                                            <div className="flex flex-row flex-wrap items-center justify-start gap-2 text-xs">
                                                {sources?.map((source, index) => (
                                                    <div
                                                        key={index}
                                                        className={`overfow-x-auto flex w-full cursor-pointer flex-wrap rounded-[28px] px-4 py-1 ${
                                                            openSource === index ? 'bg-[#007DFF]' : 'bg-[#D7EBFD] hover:bg-[#BFE1FF]'
                                                        }`}
                                                        onClick={() => setOpenSource(openSource === index ? null : index)}
                                                    >
                                                        <p
                                                            className={`text-left text-xs font-medium ${
                                                                openSource === index ? 'text-white' : 'text-[#007DFF]'
                                                            }`}
                                                            style={{ overflowWrap: 'break-word', wordWrap: 'break-word' }}
                                                        >
                                                            {index + 1}. {source.title.substring(0, 45)}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                            {!isTheLast && (
                                <div className="flex flex-row items-center justify-start">
                                    <div className={`md:invisible ${type !== 'ERROR' ? 'group-hover:md:visible' : ''}`}>
                                        <div className="">
                                            <div
                                                className="flex items-center justify-center rounded-full p-2"
                                                style={{
                                                    backgroundColor: isCopyHovered ? '#EEEEEE' : '#ffffff'
                                                }}
                                            >
                                                {copied ? (
                                                    <CheckMark
                                                        className="cursor-pointer stroke-green-2000"
                                                        onMouseEnter={() => setIsCopyHovered(true)}
                                                        onMouseLeave={() => setIsCopyHovered(false)}
                                                    />
                                                ) : (
                                                    <Tooltip placement="top" title="Sao chép">
                                                        <Copy
                                                            className={`cursor-pointer fill-none`}
                                                            onClick={() => {
                                                                handleCopyClick(message);
                                                            }}
                                                            onMouseEnter={() => setIsCopyHovered(true)}
                                                            onMouseLeave={() => setIsCopyHovered(false)}
                                                        ></Copy>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className={`flex items-center justify-center ${!isLikeClicked ? 'md:invisible' : ''} ${
                                            feedback === 'LIKE' || type !== 'ERROR' ? 'group-hover:md:visible' : ''
                                        }`}
                                    >
                                        <div className="">
                                            <div
                                                className="flex items-center justify-center rounded-full p-2"
                                                style={{
                                                    backgroundColor: isLikeHovered
                                                        ? isLikeClicked
                                                            ? 'rgba(125, 84, 209, 0.3)'
                                                            : '#EEEEEE'
                                                        : isLikeClicked
                                                          ? 'rgba(125, 84, 209, 0.3)'
                                                          : '#ffffff'
                                                }}
                                            >
                                                <Tooltip placement="top" title="Câu trả lời tốt">
                                                    <Like
                                                        className={`cursor-pointer ${
                                                            isLikeClicked || feedback === 'LIKE'
                                                                ? 'fill-white-3000 stroke-purple-30'
                                                                : 'fill-none  stroke-gray-4000'
                                                        }`}
                                                        onClick={() => {
                                                            handleFeedback?.('LIKE');
                                                            setIsLikeClicked(true);
                                                            setIsDislikeClicked(false);
                                                        }}
                                                        onMouseEnter={() => setIsLikeHovered(true)}
                                                        onMouseLeave={() => setIsLikeHovered(false)}
                                                    ></Like>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className={`flex items-center justify-center ${!isDislikeClicked ? 'md:invisible' : ''} ${
                                            feedback === 'DISLIKE' || type !== 'ERROR' ? 'group-hover:md:visible' : ''
                                        }`}
                                    >
                                        <div className="">
                                            <div
                                                className="flex items-center justify-center rounded-full p-2"
                                                style={{
                                                    backgroundColor: isDislikeHovered
                                                        ? isDislikeClicked
                                                            ? 'rgba(248, 113, 113, 0.3)'
                                                            : '#EEEEEE'
                                                        : isDislikeClicked
                                                          ? 'rgba(248, 113, 113, 0.3)'
                                                          : '#ffffff'
                                                }}
                                            >
                                                <Tooltip placement="top" title="Câu trả lời chưa tốt">
                                                    <Dislike
                                                        className={`cursor-pointer ${
                                                            isDislikeClicked || feedback === 'DISLIKE'
                                                                ? 'fill-white-3000 stroke-red-2000'
                                                                : 'fill-none  stroke-gray-4000'
                                                        }`}
                                                        onClick={() => {
                                                            handleFeedback?.('DISLIKE');
                                                            setIsDislikeClicked(true);
                                                            setIsLikeClicked(false);
                                                        }}
                                                        onMouseEnter={() => setIsDislikeHovered(true)}
                                                        onMouseLeave={() => setIsDislikeHovered(false)}
                                                    ></Dislike>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className={`flex items-center justify-center ${!isReadClicked ? 'md:invisible' : ''} ${
                                            type !== 'ERROR' ? 'group-hover:md:visible' : ''
                                        }`}
                                    >
                                        <div className="">
                                            <div
                                                className="flex items-center justify-center rounded-full p-2"
                                                style={{
                                                    backgroundColor: isReadHovered
                                                        ? isReadClicked
                                                            ? 'rgba(248, 113, 113, 0.3)'
                                                            : '#EEEEEE'
                                                        : isReadClicked
                                                          ? 'rgba(248, 113, 113, 0.3)'
                                                          : '#ffffff'
                                                }}
                                            >
                                                <Tooltip placement="top" title={isReadClicked ? 'Dừng đọc câu trả lời' : 'Đọc câu trả lời'}>
                                                    <ReadAloud
                                                        className={`cursor-pointer ${
                                                            isReadClicked
                                                                ? 'fill-white-3000 stroke-red-2000'
                                                                : 'fill-none  stroke-gray-4000'
                                                        }`}
                                                        onClick={async () => {
                                                            if (isReadClicked) {
                                                                audio.pause();
                                                                setIsReadClicked(false);
                                                            } else {
                                                                setIsReadClicked(true);
                                                                const new_audio = new Audio(
                                                                    `${apiHost}/api/t2s/vits?answer=${message}&api_key=qLQuunfY0ess1LFmup6tBJDwXFka5Ez1SPBH2NHXeo`
                                                                );
                                                                setAudio(new_audio);
                                                                new_audio.play();
                                                                new_audio.addEventListener('ended', () => {
                                                                    setIsReadClicked(false);
                                                                });
                                                            }
                                                        }}
                                                        onMouseEnter={() => setIsReadHovered(true)}
                                                        onMouseLeave={() => setIsReadHovered(false)}
                                                    ></ReadAloud>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {isTheLast && (
                                <div className="flex flex-row items-center justify-start">
                                    <div className={`md:visible ${type !== 'ERROR' ? 'group-hover:md:visible' : ''}`}>
                                        <div className="">
                                            <div
                                                className="flex items-center justify-center rounded-full p-2"
                                                style={{
                                                    backgroundColor: isCopyHovered ? '#EEEEEE' : '#ffffff'
                                                }}
                                            >
                                                {copied ? (
                                                    <CheckMark
                                                        className="cursor-pointer stroke-green-2000"
                                                        onMouseEnter={() => setIsCopyHovered(true)}
                                                        onMouseLeave={() => setIsCopyHovered(false)}
                                                    />
                                                ) : (
                                                    <Tooltip placement="top" title="Sao chép">
                                                        <Copy
                                                            className={`cursor-pointer fill-none`}
                                                            onClick={() => {
                                                                handleCopyClick(message);
                                                            }}
                                                            onMouseEnter={() => setIsCopyHovered(true)}
                                                            onMouseLeave={() => setIsCopyHovered(false)}
                                                        ></Copy>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className={`flex items-center justify-center ${!isLikeClicked ? 'md:visible' : ''} ${
                                            feedback === 'LIKE' || type !== 'ERROR' ? 'group-hover:md:visible' : ''
                                        }`}
                                    >
                                        <div className="">
                                            <div
                                                className="flex items-center justify-center rounded-full p-2"
                                                style={{
                                                    backgroundColor: isLikeHovered
                                                        ? isLikeClicked
                                                            ? 'rgba(125, 84, 209, 0.3)'
                                                            : '#EEEEEE'
                                                        : isLikeClicked
                                                          ? 'rgba(125, 84, 209, 0.3)'
                                                          : '#ffffff'
                                                }}
                                            >
                                                <Tooltip placement="top" title="Câu trả lời tốt">
                                                    <Like
                                                        className={`cursor-pointer ${
                                                            isLikeClicked || feedback === 'LIKE'
                                                                ? 'fill-white-3000 stroke-purple-30'
                                                                : 'fill-none  stroke-gray-4000'
                                                        }`}
                                                        onClick={() => {
                                                            handleFeedback?.('LIKE');
                                                            setIsLikeClicked(true);
                                                            setIsDislikeClicked(false);
                                                        }}
                                                        onMouseEnter={() => setIsLikeHovered(true)}
                                                        onMouseLeave={() => setIsLikeHovered(false)}
                                                    ></Like>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className={`flex items-center justify-center ${!isDislikeClicked ? 'md:visible' : ''} ${
                                            feedback === 'DISLIKE' || type !== 'ERROR' ? 'group-hover:md:visible' : ''
                                        }`}
                                    >
                                        <div className="">
                                            <div
                                                className="flex items-center justify-center rounded-full p-2"
                                                style={{
                                                    backgroundColor: isDislikeHovered
                                                        ? isDislikeClicked
                                                            ? 'rgba(248, 113, 113, 0.3)'
                                                            : '#EEEEEE'
                                                        : isDislikeClicked
                                                          ? 'rgba(248, 113, 113, 0.3)'
                                                          : '#ffffff'
                                                }}
                                            >
                                                <Tooltip placement="top" title="Câu trả lời chưa tốt">
                                                    <Dislike
                                                        className={`cursor-pointer ${
                                                            isDislikeClicked || feedback === 'DISLIKE'
                                                                ? 'fill-white-3000 stroke-red-2000'
                                                                : 'fill-none  stroke-gray-4000'
                                                        }`}
                                                        onClick={() => {
                                                            handleFeedback?.('DISLIKE');
                                                            setIsDislikeClicked(true);
                                                            setIsLikeClicked(false);
                                                        }}
                                                        onMouseEnter={() => setIsDislikeHovered(true)}
                                                        onMouseLeave={() => setIsDislikeHovered(false)}
                                                    ></Dislike>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className={`flex items-center justify-center ${!isReadClicked ? 'md:visible' : ''} ${
                                            type !== 'ERROR' ? 'group-hover:md:visible' : ''
                                        }`}
                                    >
                                        <div className="">
                                            <div
                                                className="flex items-center justify-center rounded-full p-2"
                                                style={{
                                                    backgroundColor: isReadHovered
                                                        ? isReadClicked
                                                            ? 'rgba(248, 113, 113, 0.3)'
                                                            : '#EEEEEE'
                                                        : isReadClicked
                                                          ? 'rgba(248, 113, 113, 0.3)'
                                                          : '#ffffff'
                                                }}
                                            >
                                                <Tooltip placement="top" title={isReadClicked ? 'Dừng đọc câu trả lời' : 'Đọc câu trả lời'}>
                                                    <ReadAloud
                                                        className={`cursor-pointer ${
                                                            isReadClicked
                                                                ? 'fill-white-3000 stroke-red-2000'
                                                                : 'fill-none  stroke-gray-4000'
                                                        }`}
                                                        onClick={async () => {
                                                            if (isReadClicked) {
                                                                audio.pause();
                                                                setIsReadClicked(false);
                                                            } else {
                                                                setIsReadClicked(true);
                                                                const new_audio = new Audio(
                                                                    `${apiHost}/api/t2s/vits?answer=${message}&api_key=qLQuunfY0ess1LFmup6tBJDwXFka5Ez1SPBH2NHXeo`
                                                                );
                                                                setAudio(new_audio);
                                                                new_audio.play();
                                                                new_audio.addEventListener('ended', () => {
                                                                    setIsReadClicked(false);
                                                                });
                                                            }
                                                        }}
                                                        onMouseEnter={() => setIsReadHovered(true)}
                                                        onMouseLeave={() => setIsReadHovered(false)}
                                                    ></ReadAloud>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {sources && openSource !== null && sources[openSource] && (
                        <div className="mt-2 max-w-[800px] rounded-xl bg-blue-200 p-2 md:ml-10">
                            <p className="m-1 w-3/4 truncate text-xs text-gray-500">Source: {sources[openSource].title}</p>

                            <div className="m-2 rounded-xl border-2 border-gray-200 bg-white p-2">
                                <p className="text-black">{sources[openSource].text}</p>
                            </div>
                        </div>
                    )}
                </div>
            </>
        );
    }
    return bubble;
});

export default ConversationBubble;
